import { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComplianceReadiness = () => {
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  const questions = [
    { id: 'q1', question: 'Does your organization have a documented ESG policy?' },
    { id: 'q2', question: 'Do you track and measure carbon emissions regularly?' },
    { id: 'q3', question: 'Have you conducted an ESG materiality assessment?' },
    { id: 'q4', question: 'Do you have a supplier code of conduct in place?' },
    { id: 'q5', question: 'Have you published a sustainability report in the past year?' },
    { id: 'q6', question: 'Do you have targets for reducing environmental impact?' },
    { id: 'q7', question: 'Is there a dedicated person/team responsible for ESG?' },
    { id: 'q8', question: 'Do you engage with stakeholders on ESG topics?' },
    { id: 'q9', question: 'Have you conducted a climate risk assessment?' },
    { id: 'q10', question: 'Do you monitor water and energy consumption?' }
  ];

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/compliance`);
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching compliance results:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const answerArray = Object.entries(answers).map(([id, answer]) => {
      const question = questions.find(q => q.id === id);
      return {
        question: question.question,
        answer: answer
      };
    });

    try {
      await axios.post(`${API}/compliance`, answerArray);
      await fetchResults();
      toast.success('Compliance check completed', {
        description: 'Your readiness score has been calculated'
      });
    } catch (error) {
      console.error('Error submitting compliance check:', error);
      toast.error('Failed to submit compliance check');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-[#2D4A3E]';
    if (score >= 40) return 'text-[#C85A32]';
    return 'text-[#A33B3B]';
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return 'Good Progress';
    if (score >= 40) return 'Getting Started';
    return 'Needs Attention';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A362D]"></div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="compliance-readiness-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-[#111814] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Compliance Readiness
        </h1>
        <p className="text-base text-[#4B5563] leading-relaxed">
          Assess your organization's readiness for ESG compliance and reporting
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Questionnaire */}
        <div className="lg:col-span-2">
          <Card className="border-[#E5E1D8]">
            <CardHeader>
              <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Compliance Checklist</CardTitle>
              <CardDescription>Answer these questions to evaluate your ESG compliance readiness</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="compliance-form">
                {questions.map((q, index) => (
                  <div key={q.id} className="p-4 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
                    <Label className="text-base font-medium text-[#111814] mb-3 block">
                      {index + 1}. {q.question}
                    </Label>
                    <RadioGroup
                      value={answers[q.id] || ''}
                      onValueChange={(value) => setAnswers({ ...answers, [q.id]: value })}
                      data-testid={`compliance-question-${q.id}`}
                    >
                      <div className="flex gap-6">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="Yes" id={`${q.id}-yes`} data-testid={`${q.id}-yes`} />
                          <Label htmlFor={`${q.id}-yes`} className="cursor-pointer text-[#4B5563]">
                            Yes
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="No" id={`${q.id}-no`} data-testid={`${q.id}-no`} />
                          <Label htmlFor={`${q.id}-no`} className="cursor-pointer text-[#4B5563]">
                            No
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="In Progress" id={`${q.id}-progress`} data-testid={`${q.id}-in-progress`} />
                          <Label htmlFor={`${q.id}-progress`} className="cursor-pointer text-[#4B5563]">
                            In Progress
                          </Label>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>
                ))}
                <Button
                  type="submit"
                  className="w-full bg-[#1A362D] hover:bg-[#2D4A3E]"
                  disabled={Object.keys(answers).length < questions.length}
                  data-testid="submit-compliance-button"
                >
                  Calculate Readiness Score
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Results Panel */}
        <div>
          {/* Readiness Score */}
          <Card className="border-[#E5E1D8] mb-6" data-testid="readiness-score-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Readiness Score</CardTitle>
            </CardHeader>
            <CardContent>
              {results ? (
                <div className="text-center">
                  <div className="mb-4">
                    <p className={`text-6xl font-bold ${getScoreColor(results.readiness_score)}`}>
                      {results.readiness_score}%
                    </p>
                    <p className="text-lg font-semibold text-[#111814] mt-2">
                      {getScoreLabel(results.readiness_score)}
                    </p>
                  </div>
                  <div className="w-full bg-[#F2F0EB] rounded-full h-3">
                    <div
                      className="bg-[#1A362D] h-3 rounded-full transition-all duration-1000"
                      style={{ width: `${results.readiness_score}%` }}
                    ></div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="w-16 h-16 text-[#6B7280] mx-auto mb-3" />
                  <p className="text-sm text-[#6B7280]">Complete the checklist to see your score</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Completed Items */}
          {results && results.completed_items.length > 0 && (
            <Card className="border-[#E5E1D8] mb-6" data-testid="completed-items-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#2D4A3E]" />
                  <CardTitle className="text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Completed</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {results.completed_items.map((item, index) => (
                    <div key={index} className="flex items-start gap-2 p-2 bg-[#2D4A3E]/5 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-[#2D4A3E] mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-[#111814]">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Missing Items */}
          {results && results.missing_items.length > 0 && (
            <Card className="border-[#E5E1D8]" data-testid="missing-items-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-[#C85A32]" />
                  <CardTitle className="text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Action Required</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {results.missing_items.map((item, index) => (
                    <div key={index} className="flex items-start gap-2 p-2 bg-[#C85A32]/5 rounded-lg">
                      <XCircle className="w-4 h-4 text-[#C85A32] mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-[#111814]">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Recommendations */}
      {results && (
        <Card className="border-[#E5E1D8] mt-8" data-testid="recommendations-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Next Steps</CardTitle>
            <CardDescription>Recommended actions to improve your compliance readiness</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
                <h4 className="font-semibold text-[#111814] mb-2">Priority 1: Documentation</h4>
                <p className="text-sm text-[#4B5563] leading-relaxed">
                  Start by documenting your ESG policy and creating a framework for tracking key metrics. This forms the foundation of compliance.
                </p>
              </div>
              <div className="p-4 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
                <h4 className="font-semibold text-[#111814] mb-2">Priority 2: Measurement</h4>
                <p className="text-sm text-[#4B5563] leading-relaxed">
                  Implement systems to measure and track your carbon footprint, water usage, and waste generation regularly.
                </p>
              </div>
              <div className="p-4 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
                <h4 className="font-semibold text-[#111814] mb-2">Priority 3: Reporting</h4>
                <p className="text-sm text-[#4B5563] leading-relaxed">
                  Prepare for external reporting by aligning with frameworks like GRI, SASB, or TCFD based on your industry.
                </p>
              </div>
              <div className="p-4 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
                <h4 className="font-semibold text-[#111814] mb-2">Priority 4: Engagement</h4>
                <p className="text-sm text-[#4B5563] leading-relaxed">
                  Engage stakeholders including employees, suppliers, and customers in your ESG journey for broader impact.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ComplianceReadiness;
